import api from "./index";

/**
 * Buscar áreas (totems) de um evento com status de range configurado
 * Endpoint: GET /event-access-range/events/{eventId}/totems
 */
export const getEventAreas = async (token, eventId) => {
  const response = await api.get(`/event-access-range/events/${eventId}/totems`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  // Retornar totems e informações de limite
  return {
    totems: response.data.totems.map((totem) => ({
      value: totem.id,
      label: totem.areaName,
      hasRangeConfigured: totem.hasRangeConfigured,
      startPosition: totem.startPosition,
      endPosition: totem.endPosition,
    })),
    totalPositionsLimit: response.data.totalPositionsLimit || null,
    totalPositionsConfigured: response.data.totalPositionsConfigured || 0,
  };
};

/**
 * Buscar configurações de range de um evento
 * Endpoint: GET /event-access-range/events/{eventId}
 */
export const getPositionConfigurations = async (token, eventId) => {
  const response = await api.get(`/event-access-range/events/${eventId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  // Mapear resposta do backend para formato esperado pelo frontend
  return {
    totalPositions: response.data.totalPositionsLimit || null,
    totalPositionsConfigured: response.data.totalPositionsConfigured || 0,
    configurations: (response.data.ranges || []).map((range) => ({
      id: range.id,
      areaId: range.eventAccessAreaId,
      areaName: range.areaName,
      positionStart: range.startPosition,
      positionEnd: range.endPosition,
      canEdit: range.canEdit,
      canDelete: range.canDelete,
      hasActiveSessions: !range.canEdit || !range.canDelete,
      hasPrintedCredentials: false, // Backend não retorna essa info
    })),
  };
};

/**
 * REMOVIDO: createPositionConfiguration, updatePositionConfiguration, deletePositionConfiguration
 * Agora usamos apenas batchPositionConfiguration (PUT) para todas as operações
 * O backend implementa semântica "replace all" - enviamos o estado desejado completo
 */

/**
 * Configurar ranges em lote com semântica "replace all" (idempotente)
 * Endpoint: PUT /event-access-range/batch
 * 
 * Backend deleta ranges não presentes e cria/atualiza os enviados.
 * 
 * Exemplos:
 * - {eventId: 27, totalPositionsLimit: 20, ranges: [...]}: configura limite e ranges
 * - {eventId: 27, totalPositionsLimit: null}: reseta para validação legado
 * - {eventId: 27}: reseta para validação legado (mesmo efeito)
 * - {eventId: 27, totalPositionsLimit: 0}: reseta para validação legado
 */
export const batchPositionConfiguration = async (token, eventId, totalPositionsLimit, ranges) => {
  const payload = {
    eventId,
    totalPositionsLimit: totalPositionsLimit ? parseInt(totalPositionsLimit, 10) : null,
    ranges: ranges && ranges.length > 0 ? ranges.map((range) => ({
      eventId,
      eventAccessAreaId: range.areaId,
      startPosition: parseInt(range.positionStart, 10),
      endPosition: parseInt(range.positionEnd, 10),
    })) : null,
  };

  const response = await api.put("/event-access-range/batch", payload, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  return response.data;
};

/**
 * Listar configurações de posições por evento
 * Endpoint: GET /event-access-range
 */
export const listEventAccessRanges = async (token) => {
  const response = await api.get("/event-access-range", {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return response.data.items || [];
};

/**
 * Excluir configurações de posições de um evento
 * Endpoint: DELETE /event-access-range/events/{eventId}
 * Se houver sessões ativas, backend retorna 409 (-50028) e exige ?confirm=true.
 */
export const deleteEventAccessRange = async (token, eventId, confirm = false) => {
  const response = await api.delete(
    `/event-access-range/events/${eventId}${confirm ? "?confirm=true" : ""}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  return response.data;
};